import { gql } from '@apollo/client';

import { CORE_ASSET_FIELDS } from 'app/src/components/Assets/queries';

export const OFFER_FIELDS = gql`
  fragment OfferFields on Offer {
    id
    saleAsset { id }
    buyer {
      id
      name
      companyName
      displayName
      fullDisplayName
      street
      city
      state
      postalCode { formatted }
      email
      phoneNumber { formatted }
    }
    original { id }
    prior { id }
    paddleServiceId
    paddleNumber
    price
    validatedMethod
    validatedAt
    isIf
    counterPrice
    soldPrice
    result
  }
`;

export const SALE_ASSET_CORE_FIELDS = gql`
  fragment SaleAssetCoreFields on SaleAsset {
    id
    status
    lotNumber
    lotNumberPrefix
    lotNumberYear
    lotNumberSuffix
    lotNumberSort
    manualRunOrder
    enteredOn
    addOn
    release
    releaseConfirmed
    soarrAdded
    paddleServicesAdded
    reserveAbsolute
    reservePrice
    sellerNet
    sellerAchInstRec
    sellerAchOn
    commissionPaidOn
    bos
    bosOn
    pickedUpOn
    releaseHold
    invoiceSentOn
    invoiceNumber
    invoiceAmount
    invoiceTxnId
    sellerInvoiceTxnId
    buyerNet
    buyerWireAmount
    buyerWireOn
    incomingWireTitle
    notes
    destroyed
    biddingStatus
    biddingNextManual
    biddingStep
    preBidStartPrice
    sellerPrice
    correctedLotNumber
  }
`;

const SALE_ASSET_FIELDS = gql`
  ${SALE_ASSET_CORE_FIELDS}
  ${OFFER_FIELDS}
  fragment SaleAssetFields on SaleAsset {
    ...SaleAssetCoreFields
    sale { id, name }
    asset {
      ${CORE_ASSET_FIELDS}
      seller {
        id
        name
        clientNumberLabelFull
        releaseOption
      }
      location {
        id
        street
        city
        state
      }
      street
      city
      state
      latestTitleLog { status }
    }
    activeOffer {
      ...OfferFields
    }
    customNotificationsCount
    sellerFees
    buyerFees
  }
`;

export const SALE_ASSETS = gql`
  ${SALE_ASSET_FIELDS}
  query SaleAssets(
    $saleId: ID
  ) {
    saleAssets(
      saleId: $saleId
    ) {
      ...SaleAssetFields
    }
  }
`;

export const SALE_ASSET_PRE_BID_FIELDS = gql`
  fragment SaleAssetPreBidFields on SaleAsset {
    highPreBidPrice
    highPreBid {
      id
      price
      bidId
      buyerUser {
        id
        fullName
        paddleNumber
        buyer { id, name, cityState }
      }
    }
    preBids {
      id
      type
      price
      createdAt
      updatedAt
      buyerUser {
        id
        fullName
        paddleNumber
        buyer { id, name, cityState }
      }
    }
  }
`;

export const SALE_ASSET_PRE_BIDS = gql`
  ${SALE_ASSET_CORE_FIELDS}
  ${SALE_ASSET_PRE_BID_FIELDS}
  query SaleAssetsPreBids(
    $saleId: ID!
  ) {
    sales {
      id
      name
    }
    saleAssets(
      saleId: $saleId
      scopes: ["include_pre_bids"]
    ) {
      ...SaleAssetCoreFields
      ...SaleAssetPreBidFields
      asset {
        ${CORE_ASSET_FIELDS}
      }
    }
  }
`;

export const SALE_ASSET_UPDATED = gql`
  ${SALE_ASSET_FIELDS}
  subscription SaleAssetUpdated (
    $saleId: ID
  ) {
    saleAssetUpdated(
      saleId: $saleId
    ) {
      saleAsset {
        ...SaleAssetFields
      }
    }
  }
`;

export const UPDATE_SALE_ASSET = gql`
  ${SALE_ASSET_CORE_FIELDS}
  mutation UpdateSaleAsset(
    $id: ID!
    $lotNumberPrefix: String
    $lotNumberYear: Int
    $lotNumberSuffix: Int
    $manualRunOrder: Int
    $enteredOn: ISO8601Date
    $addOn: ISO8601Date
    $paddleServiceCode: String
    $paddleServiceAdded: Boolean
    $release: Boolean
    $releaseConfirmed: Boolean
    $soarrAdded: Boolean
    $bos: Boolean
    $releaseHold: Boolean
    $invoiceNumber: String
    $invoiceAmount: Decimal
    $buyerWireAmount: Decimal
    $buyerWireOn: ISO8601Date
    $incomingWireTitle: String
    $reservePrice: Decimal
    $reserveAbsolute: Boolean
    $sellerAchInstRec: String
    $sellerAchOn: ISO8601Date
    $notes: String
    $assetAttributes: AssetInput
    $offerAttributes: OfferInput
    $remove: Boolean
    $biddingStatus: String
    $biddingStep: Int
    $biddingNextManual: Int
    $preBidStartPrice: Decimal
    $sellerPrice: Decimal
    $status: String
  ) {
    updateSaleAsset(
      id: $id
      lotNumberPrefix: $lotNumberPrefix
      lotNumberYear: $lotNumberYear
      lotNumberSuffix: $lotNumberSuffix
      manualRunOrder: $manualRunOrder
      enteredOn: $enteredOn
      addOn: $addOn
      paddleServiceCode: $paddleServiceCode
      paddleServiceAdded: $paddleServiceAdded
      release: $release
      releaseConfirmed: $releaseConfirmed
      invoiceNumber: $invoiceNumber
      invoiceAmount: $invoiceAmount
      buyerWireAmount: $buyerWireAmount
      buyerWireOn: $buyerWireOn
      incomingWireTitle: $incomingWireTitle
      soarrAdded: $soarrAdded
      bos: $bos
      releaseHold: $releaseHold
      reservePrice: $reservePrice
      reserveAbsolute: $reserveAbsolute
      sellerAchInstRec: $sellerAchInstRec
      sellerAchOn: $sellerAchOn
      notes: $notes
      assetAttributes: $assetAttributes
      offerAttributes: $offerAttributes
      remove: $remove
      biddingStatus: $biddingStatus
      biddingStep: $biddingStep
      biddingNextManual: $biddingNextManual
      preBidStartPrice: $preBidStartPrice
      sellerPrice: $sellerPrice
      status: $status
    ) {
      saleAsset {
        ...SaleAssetCoreFields
      }
      errors {
        path
        message
      }
    }
  }
`;

export const UPDATE_ASSET = gql`
  mutation UpdateAsset(
    $id: ID!
    $buyerId: ID
  ) {
    updateAsset(
      id: $id
      buyerId: $buyerId
    ) {
      asset {
        id
        name
        buyer { id, name }
      }
    }
  }
`;

export const CLEAR_OFFER_RESULT = gql`
  mutation ClearOfferResult($saleAssetId: ID!) {
    clearOfferResult(saleAssetId: $saleAssetId) {
      offer { id, result }
    }
  }
`;

export const UPDATE_LAST_OFFER_PRICE = gql`
  mutation UpdateLastOfferPrice(
    $saleAssetId: ID!
    $price: Decimal
    $isIf: Boolean
  ) {
    updateLastOfferPrice(
      saleAssetId: $saleAssetId
      price: $price
      isIf: $isIf
    ) {
      offer { id, result }
    }
  }
`;

export const REINSTATE_SALE_ASSET = gql`
  mutation ReinstateSaleAsset($id: ID!) {
    reinstateSaleAsset(id: $id) {
      saleAsset {
        id
        status
      }
    }
  }
`;

export const ADD_TO_ACH_BATCH = gql`
  mutation AddToAchBatch($id: ID!) {
    addToAchBatch(id: $id) {
      saleAsset {
        id
      }
    }
  }
`;